import React, {useEffect, useState} from 'react';
import Table from '../../../components/common/table/Table';
import {Column} from '../../common/table/TableSection';
import {useNavigate} from "react-router-dom";
import {DocumentTableResponseData} from "../../../types/client";
import {documentTableAPI} from "../../../services/client";
import {DocumentSort, DocumentTarget} from "../../../types/document";
import styles from './ClientDocumentTable.module.css';

interface ExtendedColumn<T> extends Column<T> {
    render?: (value: any, row: T, index: number) => React.ReactNode;
}
//클라이언트 문서 테이블
const ClientDocumentTable = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<DocumentTableResponseData[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [pendingSearchTerm, setPendingSearchTerm] = useState('');
    const [searchType, setSearchType] = useState<DocumentTarget>(DocumentTarget.ALL);

    //테이블 로우 선택 시
    const handleRowClick = (row: DocumentTableResponseData) => {
        navigate(`/client/documents/${row.documentId}`);
    };

    //정렬 변경
    const handleSortChange = (field: string | null, direction: 'asc' | 'desc' | null) => {
        setSort({field, direction});
    };

    //정렬
    const [sort, setSort] = useState<{ field: string | null, direction: 'asc' | 'desc' | null }>({
        field: 'createdAt',
        direction: 'desc'
    });

    //컬럼
    const columns: ExtendedColumn<DocumentTableResponseData>[] = [
        {
            key: 'documentId',
            header: 'No.',
            width: '10%',
            render: (value, row, index) => ((page - 1) * 7) + index + 1
        },
        {
            key: 'title',
            header: '제목',
            width: '60%'
        },
        {
            key: 'createdAt',
            header: '작성일',
            width: '30%',
            sortable: true,
            render: (value) => {
                const date = new Date(value);
                return `${date.getFullYear().toString().slice(2)}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')} (${['일', '월', '화', '수', '목', '금', '토'][date.getDay()]})`;
            }
        }
    ];

    //정렬 기준 설정
    const getSortParam = (field: string | null, direction: 'asc' | 'desc' | null): DocumentSort => {
        if (!field || !direction) return DocumentSort.CREATE_DESC;
        const sortMap: Record<string, Record<string, DocumentSort>> = {
            createdAt: {
                asc: DocumentSort.CREATE_ASC,
                desc: DocumentSort.CREATE_DESC
            }
        };
        return sortMap[field]?.[direction] || DocumentSort.CREATE_DESC;
    }

    //검색
    const handleSearch = () => {
        setSearchTerm(pendingSearchTerm);
        setPage(1);
    };

    //엔터키 사용 검색
    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    //데이터 설정
    const fetchData = async () => {
        try {
            setLoading(true);
            //클라이너트 문서 테이블 조회 API
            const response = await documentTableAPI({
                page: page - 1,
                sort: getSortParam(sort.field, sort.direction),
                search: searchTerm,
                target: searchType
            });
            //데이터 설정
            setData(response.content);
            //페이지 설정
            setTotalPages(Math.ceil(response.total / 7));
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [page, searchTerm, searchType, sort]);

    //검색 대상 설정
    const searchOptions = [
        {value: DocumentTarget.ALL, label: '전체'},
        {value: DocumentTarget.TITLE, label: '제목'},
        {value: DocumentTarget.CONTENT, label: '내용'}
    ];

    return (
        <div className={styles.container}>
            <div className={styles.searchContainer}>
                <div>
                    {/*검색 대상 선택*/}
                    <select
                        value={searchType}
                        onChange={(e) => setSearchType(e.target.value as DocumentTarget)}
                        className={styles.searchTypeSelect}
                    >
                        {searchOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className={styles.searchInputWrapper}>
                    {/*검색 창*/}
                    <input
                        type="text"
                        placeholder="검색어를 입력하세요"
                        value={pendingSearchTerm}
                        onChange={(e) => setPendingSearchTerm(e.target.value)}
                        onKeyPress={handleKeyPress}
                        className={styles.searchInput}
                    />
                    {/*검색 버튼*/}
                    <button
                        onClick={handleSearch}
                        className={styles.searchButton}
                        aria-label="Search"
                    >
                        검색
                    </button>
                </div>
            </div>
            {/*테이블*/}
            <Table
                columns={columns}
                data={data}
                loading={loading}
                size="medium"
                showPagination={true}
                page={page}
                pageSize={7}
                totalItems={totalPages * 7}
                onPageChange={setPage}
                onSortChange={handleSortChange}
                onRowClick={handleRowClick}
            />
        </div>
    );
};

export default ClientDocumentTable;